import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, forwardRef } from 'react';

import { cn } from '@natu/utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center font-semibold leading-[24px] tracking-[-0.2px] transition-colors focus-visible:outline-none disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary: cn(
          'bg-primary-600 border-primary-600 rounded-full border uppercase text-white',
          'hover:bg-naturaily-butterscotch hover:border-naturaily-butterscotch',
          'disabled:bg-primary-300 disabled:border-primary-300',
          'focus-visible:bg-naturaily-butterscotch focus-visible:border-naturaily-butterscotch focus-visible:ring-primary-600 focus-visible:ring-2 focus-visible:ring-offset-2',
        ),
        'primary-white': cn(
          'text-primary-600 rounded-full border border-white bg-white uppercase',
          'hover:bg-naturaily-butterscotch hover:border-naturaily-butterscotch hover:text-white',
          'disabled:text-primary-300 disabled:border-primary-300',
          'focus-visible:bg-naturaily-butterscotch focus-visible:border-naturaily-butterscotch focus-visible:ring-offset-primary-600 focus-visible:text-white focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2',
        ),
        outline: cn(
          'text-primary-600 border-primary-600 rounded-full border bg-white uppercase',
          'hover:bg-primary-600 hover:border-white hover:text-white',
          'disabled:text-primary-300 disabled:border-primary-300',
          'focus-visible:bg-primary-600 focus-visible:ring-primary-600 focus-visible:text-white focus-visible:ring-2 focus-visible:ring-offset-2',
        ),
        'outline-butterscotch': cn(
          'text-naturaily-butterscotch border-naturaily-butterscotch rounded-full border bg-transparent uppercase',
          'hover:bg-naturaily-butterscotch hover:border-naturaily-butterscotch hover:text-white',
          'disabled:text-warning-200 disabled:border-warning-200',
          'focus-visible:bg-naturaily-butterscotch focus-visible:ring-naturaily-butterscotch focus-visible:text-white focus-visible:ring-2 focus-visible:ring-offset-2',
        ),
        link: cn(
          'text-primary-600 underline decoration-transparent underline-offset-2',
          'hover:decoration-primary-600',
          'disabled:text-primary-300',
          'focus-visible:decoration-primary-600 focus-visible:ring-primary-600 focus-visible:ring-1 focus-visible:ring-offset-2',
        ),
        'link-gray': cn(
          'text-gray-600 underline decoration-transparent underline-offset-2',
          'hover:decoration-gray-600',
          'disabled:text-gray-300',
          'focus-visible:decoration-gray-600 focus-visible:ring-1 focus-visible:ring-gray-600 focus-visible:ring-offset-2',
        ),
        'link-lightgray': cn(
          'text-gray-400 underline decoration-transparent underline-offset-2',
          'hover:decoration-gray-400',
          'disabled:text-gray-300',
          'focus-visible:decoration-gray-400 focus-visible:ring-1 focus-visible:ring-gray-400 focus-visible:ring-offset-2',
        ),
        'link-white': cn(
          'text-white underline decoration-transparent underline-offset-2',
          'hover:decoration-white',
          'disabled:text-primary-200',
          'focus-visible:ring-offset-primary-600 focus-visible:decoration-white focus-visible:ring-1 focus-visible:ring-white focus-visible:ring-offset-2',
        ),
        'link-light': cn(
          'text-primary-200 underline decoration-transparent underline-offset-2',
          'hover:decoration-primary-200',
          'focus-visible:decoration-primary-200 focus-visible:ring-primary-200 focus-visible:ring-1 focus-visible:ring-offset-2',
        ),
        'outline-transparent': cn(
          'text-primary-600 border-primary-600 rounded-full border bg-transparent uppercase',
          'hover:bg-primary-600 hover:border-primary-600 hover:text-white',
          'disabled:text-primary-300',
          'focus-visible:bg-primary-600 focus-visible:ring-primary-600 focus-visible:text-white focus-visible:ring-2 focus-visible:ring-offset-2',
        ),
        ghost: cn(
          'rounded-full border border-transparent bg-transparent text-gray-900',
          'hover:border-gray-100 hover:bg-gray-100',
          'disabled:text-gray-300',
          'focus-visible:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-100 focus-visible:ring-offset-2',
        ),
        tertiary: cn(
          'text-primary-600 rounded-full border border-transparent bg-transparent',
          'hover:border-gray-100 hover:bg-gray-100',
          'disabled:text-primary-300',
          'focus-visible:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-100 focus-visible:ring-offset-2',
        ),
      },
      size: {
        '2xl': 'px-7 py-4',
        sm: 'px-3.5 py-2',
        xl: 'px-5 py-3',
        lg: 'px-[18px] py-[10px]',
        md: 'px-5 py-[10px]',
        link: 'p-0',
        icon: 'h-10 !w-10 rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'xl',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        style={{ textDecorationSkipInk: 'none' }}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
